import {
  useState,
  useEffect,
  FormEvent,
  ChangeEvent,
  ReactElement,
} from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Icon } from '@/components/ui/icon'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AuthPageFrame, AuthDivider } from '@/components/auth/AuthPageFrame'
import { InvitationClosedNotice } from '@/components/auth/InvitationClosedNotice'
import { GoogleMark, AppleMark } from '@/components/auth/ProviderMarks'
import {
  faEnvelope,
  faCircleExclamation,
  faEye,
  faEyeSlash,
} from '@awesome.me/kit-e215ba4646/icons/sharp/light'
import {
  getInvitationById,
  acceptOAuthInvitation,
} from '../services/enhancedInvitations'
import { registerWithMinimalData, signInWithEmail } from '../services/auth'
import { useOAuthInvitation } from '@/hooks/useOAuthInvitation'
import { toast } from 'sonner'
import { UserRoles, roleLabel, type UserRole } from '../constants/userRoles'
import { logger } from '@/services/logger'
import { APPLE_SIGN_IN_ENABLED } from '@/constants/authProviders'
import { afterAcceptPath } from '@/services/twoFactor'
import { reportAuthFailure } from '@/services/authFailureLog'
import { Spinner } from '@/components/ui/spinner'

interface Invitation {
  id: string
  email: string
  firstName: string
  lastName: string
  role: UserRole
  status: 'pending' | 'accepted' | 'expired' | 'revoked'
  corporateAccountId?: string
  invitedBy?: string
}

export default function AcceptInvitation(): ReactElement {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const invitationId = searchParams.get('id')

  const [invitation, setInvitation] = useState<Invitation | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showEmailSignup, setShowEmailSignup] = useState(false)
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  // Use unified OAuth hook for all providers
  const {
    handleOAuthSignIn,
    submitting: oauthSubmitting,
    error: oauthError,
  } = useOAuthInvitation({ invitationId, role: invitation?.role })

  // Track overall submitting state (OAuth or email)
  const [emailSubmitting, setEmailSubmitting] = useState(false)
  const submitting = oauthSubmitting || emailSubmitting

  useEffect(() => {
    loadInvitation()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invitationId])

  const loadInvitation = async () => {
    if (!invitationId) {
      setError('Invalid invitation link')
      setLoading(false)
      return
    }

    try {
      const inv = await getInvitationById(invitationId)

      if (!inv) {
        setError('Invitation not found')
      } else if (inv.status === 'accepted') {
        setError('This invitation has already been accepted')
      } else if (inv.status === 'expired') {
        setError('This invitation has expired')
      } else if (inv.status === 'revoked') {
        setError('This invitation has been cancelled')
      } else {
        setInvitation(inv as Invitation)
      }
    } catch (err) {
      console.error('Error loading invitation:', err)
      reportAuthFailure('accept_invitation', err)
      setError('Failed to load invitation')
    }

    setLoading(false)
  }

  const handleEmailSignup = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setPasswordError('')
    setEmailSubmitting(true)

    if (!invitation || !invitationId) {
      setPasswordError('Invalid invitation')
      setEmailSubmitting(false)
      return
    }

    try {
      // Validate password
      if (password.length < 8) {
        setPasswordError('Password must be at least 8 characters')
        setEmailSubmitting(false)
        return
      }

      if (password !== confirmPassword) {
        setPasswordError('Passwords do not match')
        setEmailSubmitting(false)
        return
      }

      // Try to create user account
      let result = await registerWithMinimalData(
        {
          firstName: invitation.firstName,
          lastName: invitation.lastName,
          email: invitation.email,
          password: password,
          role: invitation.role,
          corporateAccountId: invitation.corporateAccountId,
          invitationId,
        },
        invitation.invitedBy,
        {
          skipEmailVerification: true,
          skipUserDocument: invitation.role === UserRoles.SUPER_ADMIN,
        }
      )

      // If email already exists, try to sign in instead
      // This handles the case where a previous attempt created the Auth account
      // but failed during Firestore document creation
      if (
        !result.success &&
        result.error?.includes('auth/email-already-in-use')
      ) {
        logger.debug(
          '[AcceptInvitation] Email already in use, attempting sign in...'
        )
        result = await signInWithEmail(invitation.email, password)

        if (!result.success) {
          // If sign in fails, it might be wrong password
          setPasswordError(
            'An account with this email already exists. Please check your password or try a different sign-in method.'
          )
          setEmailSubmitting(false)
          return
        }
      }

      if (result.success && result.user) {
        // Accept the invitation
        const acceptResult = await acceptOAuthInvitation(
          invitationId,
          result.user.uid,
          result.user.email || ''
        )

        if (acceptResult.success) {
          toast.success('Welcome! Your account has been created.')
          navigate(afterAcceptPath(invitation.role))
        } else {
          reportAuthFailure(
            'accept_invitation',
            new Error(acceptResult.error),
            { email: invitation.email }
          )
          setPasswordError(acceptResult.error || 'Failed to accept invitation')
          setEmailSubmitting(false)
        }
      } else {
        reportAuthFailure('accept_invitation', new Error(result.error), {
          email: invitation.email,
        })
        setPasswordError(result.error || 'Failed to create account')
        setEmailSubmitting(false)
      }
    } catch (err) {
      console.error('Error creating account:', err)
      reportAuthFailure('accept_invitation', err, { email: invitation.email })
      const message =
        err instanceof Error ? err.message : 'Failed to create account'
      setPasswordError(message)
      setEmailSubmitting(false)
    }
  }

  const handlePasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value)
  }

  const handleConfirmPasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword(e.target.value)
  }

  if (loading) {
    return (
      <AuthPageFrame>
        <Card>
          <CardContent>
            <div className="flex items-center justify-center gap-3">
              <Spinner className="h-5 w-5" />
              <span>Loading invitation...</span>
            </div>
          </CardContent>
        </Card>
      </AuthPageFrame>
    )
  }

  if (error && !invitation) {
    return (
      <AuthPageFrame>
        <InvitationClosedNotice
          invitationId={invitationId}
          onSignIn={() => navigate('/signin')}
        />
      </AuthPageFrame>
    )
  }

  return (
    <AuthPageFrame>
      {(oauthError || passwordError) && (
        <Alert variant="destructive">
          <Icon icon={faCircleExclamation} className="h-4 w-4" />
          <AlertDescription>{oauthError || passwordError}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader className="text-center">
          <CardTitle className="text-xl">Welcome to MNTND</CardTitle>
          <CardDescription>Complete your account setup</CardDescription>
        </CardHeader>

        <CardContent>
          <div className="grid gap-6">
            {invitation && (
              <div className="rounded-lg border bg-muted/50 p-4">
                <p className="text-sm text-muted-foreground mb-2">
                  You&apos;ve been invited as:
                </p>
                <p className="font-semibold">
                  {invitation.firstName} {invitation.lastName}
                </p>
                <p className="text-sm text-muted-foreground break-all">
                  {invitation.email}
                </p>
                <p className="text-sm mt-2">
                  Role: {roleLabel(invitation.role)}
                </p>
              </div>
            )}

            {!showEmailSignup ? (
              <>
                <p className="text-center text-sm text-muted-foreground">
                  Choose a method to create your account:
                </p>

                {/* OAuth Options */}
                <div className="flex flex-col gap-4">
                  {APPLE_SIGN_IN_ENABLED && (
                    <Button
                      variant="outline"
                      type="button"
                      disabled={submitting}
                      onClick={() => handleOAuthSignIn('apple')}
                      className="w-full"
                    >
                      {submitting ? <Spinner /> : <AppleMark />}
                      Apple
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    type="button"
                    disabled={submitting}
                    onClick={() => handleOAuthSignIn('google')}
                    className="w-full"
                  >
                    {submitting ? <Spinner /> : <GoogleMark />}
                    Continue with Google
                  </Button>
                </div>

                <AuthDivider label="Or continue with" />

                {/* Email/Password Option */}
                <Button
                  variant="outline"
                  onClick={() => setShowEmailSignup(true)}
                  className="w-full"
                  disabled={submitting}
                >
                  <Icon icon={faEnvelope} className="h-4 w-4" />
                  Sign up with Email & Password
                </Button>
              </>
            ) : (
              <>
                {/* Email/Password Form */}
                <form onSubmit={handleEmailSignup} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="password">Create Password</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        autoComplete="new-password"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={handlePasswordChange}
                        placeholder="At least 8 characters"
                        required
                        minLength={8}
                        disabled={submitting}
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(prev => !prev)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        tabIndex={-1}
                      >
                        <Icon
                          icon={showPassword ? faEyeSlash : faEye}
                          className="h-4 w-4"
                        />
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm Password</Label>
                    <div className="relative">
                      <Input
                        id="confirmPassword"
                        autoComplete="new-password"
                        type={showPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={handleConfirmPasswordChange}
                        placeholder="Re-enter your password"
                        required
                        minLength={8}
                        disabled={submitting}
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(prev => !prev)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        tabIndex={-1}
                      >
                        <Icon
                          icon={showPassword ? faEyeSlash : faEye}
                          className="h-4 w-4"
                        />
                      </button>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full"
                    disabled={submitting || !password || !confirmPassword}
                  >
                    {submitting ? (
                      <>
                        <Spinner className="mr-2" />
                        Creating Account...
                      </>
                    ) : (
                      'Create Account & Continue'
                    )}
                  </Button>

                  <Button
                    type="button"
                    variant="link"
                    onClick={() => setShowEmailSignup(false)}
                    className="w-full"
                    disabled={submitting}
                  >
                    Back to other options
                  </Button>
                </form>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </AuthPageFrame>
  )
}
